import type { ReactNode } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import type { Alarma } from '../../../domain/entities/Alarma';
import { formatearFechaHora } from '../../utils/formato';
import estilos from './Alarmas.module.css';

interface DetalleAlarmaProps {
  alarma: Alarma | null;
  onCerrar: () => void;
  onReconocer: (id: string) => void;
  onResolver: (id: string) => void;
  procesando: boolean;
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <>
      <span className={estilos.detalleEtiqueta}>{etiqueta}</span>
      <span className={estilos.detalleValor}>{children}</span>
    </>
  );
}

/** Ficha completa de una alarma, con las acciones disponibles al pie. */
export function DetalleAlarma({
  alarma,
  onCerrar,
  onReconocer,
  onResolver,
  procesando,
}: DetalleAlarmaProps) {
  if (!alarma) return null;

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={alarma.sensorNombre}
      descripcion={alarma.ubicacion}
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cerrar
          </Button>
          <Button
            variante="secundario"
            icono="check"
            cargando={procesando}
            motivoDeshabilitado={alarma.motivoNoReconocible()}
            onClick={() => onReconocer(alarma.id)}
          >
            Reconocer
          </Button>
          <Button
            variante="exito"
            icono="checkDoble"
            cargando={procesando}
            motivoDeshabilitado={alarma.motivoNoResoluble()}
            onClick={() => onResolver(alarma.id)}
          >
            Resolver
          </Button>
        </>
      }
    >
      <div className={estilos.detalle}>
        <div>
          <div className={estilos.detalleTitulo}>Situación</div>
          <div className={estilos.detalleGrupo}>
            <Dato etiqueta="Criticidad">
              <Badge color={alarma.criticidad.color}>{alarma.criticidad.etiqueta}</Badge>
            </Dato>
            <Dato etiqueta="Estado">
              <Badge color={alarma.estado.color}>{alarma.estado.etiqueta}</Badge>
            </Dato>
            {alarma.textoCondicion && <Dato etiqueta="Condición">{alarma.textoCondicion}</Dato>}
            <Dato etiqueta="Valor que disparó">{alarma.valorMostrable}</Dato>
            <Dato etiqueta="Umbral">{alarma.umbralMostrable}</Dato>
            {alarma.mensaje && <Dato etiqueta="Mensaje">{alarma.mensaje}</Dato>}
          </div>
        </div>

        <div>
          <div className={estilos.detalleTitulo}>Tiempos</div>
          <div className={estilos.detalleGrupo}>
            <Dato etiqueta="Inicio">{formatearFechaHora(alarma.fechaInicio)}</Dato>
            <Dato etiqueta="Cierre">{formatearFechaHora(alarma.fechaFin)}</Dato>
            <Dato etiqueta="Duración">{alarma.duracionMostrable()}</Dato>
            {alarma.fechaReconocimiento && (
              <Dato etiqueta="Reconocida">{formatearFechaHora(alarma.fechaReconocimiento)}</Dato>
            )}
            {alarma.reconocidaPor && <Dato etiqueta="Reconocida por">{alarma.reconocidaPor}</Dato>}
          </div>
        </div>

        <div>
          <div className={estilos.detalleTitulo}>Origen</div>
          <div className={estilos.detalleGrupo}>
            <Dato etiqueta="Cliente">{alarma.clienteNombre ?? '—'}</Dato>
            <Dato etiqueta="Área">{alarma.areaNombre ?? '—'}</Dato>
            <Dato etiqueta="Nodo">{alarma.nodoNombre ?? '—'}</Dato>
            <Dato etiqueta="Tipo de umbral">{alarma.tipoUmbral}</Dato>
            <Dato etiqueta="Histéresis">{alarma.histeresisMostrable}</Dato>
            <Dato etiqueta="Retención">{alarma.retenida ? 'Activada' : 'Desactivada'}</Dato>
            {alarma.escalada && (
              <Dato etiqueta="Escalada">
                {alarma.nivelEscalacion ? `Sí · nivel ${alarma.nivelEscalacion}` : 'Sí'}
              </Dato>
            )}
            {alarma.observaciones && <Dato etiqueta="Observaciones">{alarma.observaciones}</Dato>}
            <Dato etiqueta="Identificador">{alarma.idCorto}</Dato>
          </div>
        </div>
      </div>
    </Modal>
  );
}
